import { type } from 'os';
import React, { createContext, useContext, useEffect, useState } from 'react';
import isEqual from '../helpers/isEqual';
import { ApiService } from '../services/api';
import { ApiError } from '../services/api/core/ApiError';
import { FolderResponse } from '../services/api/models/FolderResponse';
import { UnitDetail } from '../services/api/models/UnitDetail';
import { useAuth } from './UserContext';

interface AccountContextData {
    units: UnitDetail[];
    getUnitById(id: number): UnitDetail | null;
    selectActiveAccount(folder: FolderResponse | null): void;
    accounts: FolderResponse[];
    activeAccount: FolderResponse | null;
    refreshing: boolean;
    refreshUnits(): Promise<void>;
    page: number | 1;
    pageLength: number | 0;
    setPage(page: number): void;
    filter: string;
    setFilter(filter: string): void;
}

const AccountContext = createContext<AccountContextData>({} as AccountContextData);

export const AccountProvider = (props: { children: React.ReactNode }) => {
  const [accounts, setAccounts] = useState<FolderResponse[]>([]);
  const [units, setUnits] = useState<UnitDetail[]>([]);
  const [activeAccount, setActiveAccount] = useState<FolderResponse | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const { isLoggedIn, logout } = useAuth();

    const [page, setPage] = useState<number>(0);
    const [pageLength, setPageLength] = useState<number>(0);
    const [filter, setFilter] = useState<string>("");
  useEffect(() => {
    if (activeAccount) {
      for (let i = 0; i < accounts.length; i++) {
        if (isEqual(activeAccount, accounts[i])) {
          return;
        }
      }
      setActiveAccount(null); //updated accounts does not contain current active one so we need to clear
    } else {
      if (accounts.length === 1) {
        setActiveAccount(accounts[0]);
      } else {
        const sessionAccount = window.sessionStorage.getItem('account');
        if (sessionAccount) { 
          setActiveAccount(accounts.find(a => a.id === Number(sessionAccount)) || null);
        }
      }
    }
  }, [accounts, activeAccount]);

    useEffect(() => {
        console.log(page)
        setUnits([]);
    if (activeAccount?.id) {
      fetchUnitsInFolder(activeAccount.id,page,filter);
      window.sessionStorage.setItem('account', String(activeAccount.id));
    }
  }, [activeAccount,page,filter]);

  useEffect(() => {
    if (isLoggedIn) {
      refreshAccounts();
    }
  }, [isLoggedIn]);

  const refreshAccounts = async (): Promise<void> => {
    try { 
      const response = await ApiService.getFolders();
      setAccounts(response);
    } catch (err:any) { 
      if (err instanceof ApiError && err.status === 401 || err.status === 403) { 
        logout();
      }
    }
  };

  const refreshUnits = async () => {
      if (activeAccount && activeAccount.id) {
          await fetchUnitsInFolder(activeAccount.id, page,filter);
    }
  }

    const fetchUnitsInFolder = async (folderId: number, nextPageNo: number,filter:string )=> {
    setRefreshing(true);
        try {
            const response = await ApiService.getUnitsInFolder(folderId, filter, nextPageNo);
        if (response.success && response.value) {
            setPageLength(response.value.noOfPages ? response.value.noOfPages : 0)
            const units = response.value.units;
            setUnits(units || []);
        }
    } catch (err:any) { 
      if (err instanceof ApiError && err.status === 401 || err.status === 403) { 
        logout();
      }
    } finally { 
      setRefreshing(false);
    }

  };

  const getUnitById = (id: number): UnitDetail | null => {
    const match = units.find(v => v.id === id);
    return match || null;
  };

  const selectActiveAccount = (account: FolderResponse | null) => {
    setActiveAccount(account);
  };

  return (
      <AccountContext.Provider value={{ units, getUnitById, selectActiveAccount, accounts, activeAccount, refreshing, refreshUnits, page, pageLength, setPage,filter, setFilter }}>
      {props.children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => {
  return useContext(AccountContext);
};
